import { Suspense, useEffect, useState, useMemo } from "react";
import { matchPath, useLocation, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo } from "@fortawesome/free-solid-svg-icons";
import { useQueryClient } from "react-query";
import { useNubsGameRefresher, useNubsTaskRefresher, useVideoRefresher } from "../Global.state";
import useRefresh, {
  challengesQueryKey,
  gameUri,
  leaderboardQueryKey,
  rewardsQueryKey,
  teamsQueryKey,
  groupsQueryKey,
  gameStreamQueryKey,
  personalizationsUri,
  analyticsQueryKey,
  infiniteAnswerCommentsQueryKey,
  nubsQueryKey,
  npcsQueryKey,
  judgeQueryKey,
  teamStreamQueryKey,
  monitorQueryKey,
  zonesQueryKey,
  designationsQueryKey,
  gameBrandingUri,
  libraryQueryKey,
  answerQueryKey,
  challengeStreamQueryKey,
  dashboardQueryKey,
  slideshowQueryKey,
  waypointsQueryKey,
  administratorsQueryKey,
  coordinatorsQueryKey,
  bannedQueryKey,
} from "../lib/refresh";
import useCurrentRouteKey from "../hooks/useCurrentRouteKey";
import { pathQueryKeyMap, realTimeUpdatesState, instantUpdateSources, hasListener } from "./Refresher.state";
import { websocketState } from "./ConsoleUi/ConsoleUi.state";
import { getEventTemplateParts } from "../lib/event-translations";
import { infiniteTeamsOptionsQueryKey } from "../teams/Teams.state";
import Callout from "./Callout";
import Spinner from "./Spinner";
import { Button } from "../App.styles";
import ROUTE_PATTERNS from "../consts/routes";
import useRefreshData from "../hooks/useRefreshData";

/**
 * Resolve the game ID from a websocket payload so cross-entity events can be scoped.
 * @param {{ detail?: object, source?: string }} message
 * @returns {string|number|null}
 */
export const getGameId = ({ detail, source }) => {
  if (detail?.game) {
    return detail.game.id;
  }

  switch (source) {
    case "ss.answer.accept":
    case "ss.answer.create":
    case "ss.answer.feature":
    case "ss.answer.like":
    case "ss.answer.unlike":
    case "ss.answer.reject":
    case "ss.answer.update":
    case "ss.comment.create":
    case "ss.comment.delete":
    case "ss.comment.like":
    case "ss.comment.unlike":
      return detail.answer.game_id;
    case "ss.reward.create":
    case "ss.reward.delete":
    case "ss.reward.update":
      return detail.reward.game_id;
    default:
      return [
        detail?.answer,
        detail?.reward,
        detail?.team,
        detail?.task,
        detail?.group,
        detail?.personalization,
        detail?.designation,
        detail?.npc,
        detail?.non_player_character,
        detail?.zone,
        detail?.waypoint,
        detail?.relation,
        detail?.administrator,
        detail?.admin,
        detail?.coordinator,
        detail?.user,
        detail?.ban,
        detail?.banned,
      ].find(entity => entity?.game_id)?.game_id || null;
  }
};

// When a websocket event is received, the app needs to invalidate the query keys affected by the event
// refresher.state.js contains the list of page paths associated with each query key.
// If a page is consuming a query key that needs updating:
// 1. If the query keys are throttled, it will show a blue bar with a refresh button. Page will refresh as soon as the refresh button is clicked.
// 2. If the query keys are not throttled, page will refresh as soon as the event is received.
export const refreshUris = {
  "ss.answer.accept": ['answerQueryKey', 'leaderboardQueryKey', 'challengeStreamQueryKey', 'teamStreamQueryKey', 'gameStreamQueryKey', 'slideshowQueryKey', 'monitorQueryKey'],
  "ss.answer.create": ['leaderboardQueryKey', 'slideshowQueryKey', 'monitorQueryKey'],
  "ss.answer.feature": ['answerQueryKey', 'leaderboardQueryKey', 'challengeStreamQueryKey', 'teamStreamQueryKey', 'gameStreamQueryKey', 'slideshowQueryKey', 'monitorQueryKey'],
  "ss.answer.unfeature": ['answerQueryKey', 'leaderboardQueryKey', 'challengeStreamQueryKey', 'teamStreamQueryKey', 'gameStreamQueryKey', 'slideshowQueryKey', 'monitorQueryKey'],
  "ss.answer.like": ['answerQueryKey', 'monitorQueryKey'],
  "ss.answer.reject": ['answerQueryKey', 'leaderboardQueryKey', 'teamStreamQueryKey', 'gameStreamQueryKey', 'slideshowQueryKey', 'monitorQueryKey'],
  "ss.answer.unlike": ['answerQueryKey', 'monitorQueryKey'],
  "ss.answer.update": ['answerQueryKey', 'monitorQueryKey', 'leaderboardQueryKey'],
  "ss.designation.create": ['designationsQueryKey'],
  "ss.designation.delete": ['designationsQueryKey'],
  "ss.designation.update": ['designationsQueryKey'],
  "ss.game.create": ['dashboardQueryKey'],
  "ss.game.clone": ['dashboardQueryKey'],
  "ss.game.delete": ['dashboardQueryKey'],
  "ss.game.export": ['gameUri'],
  "ss.game.join": ['teamsQueryKey', 'infiniteTeamsOptionsQueryKey', 'gameStreamQueryKey', 'monitorQueryKey'],
  "ss.game.media": ['gameStreamQueryKey', 'nubsQueryKey', 'judgeQueryKey'],
  "ss.game.reel": ['gameUri'],
  "ss.game.rescore": ['leaderboardQueryKey'],
  "ss.game.score": ['leaderboardQueryKey'],
  "ss.game.update": ['dashboardQueryKey', 'gameUri', 'gameBrandingUri'],
  "ss.purchase.donation": ['gameUri'],
  "ss.purchase.library": ['libraryQueryKey'],
  "ss.reward.create": ['rewardsQueryKey'],
  "ss.reward.delete": ['rewardsQueryKey'],
  "ss.reward.update": ['rewardsQueryKey'],
  "ss.task.create": ['challengesQueryKey'],
  "ss.task.delete": ['challengesQueryKey'],
  "ss.task.reel": ['gameUri'],
  "ss.task.update": ['challengesQueryKey'],
  "ss.team.create": ['teamsQueryKey', 'infiniteTeamsOptionsQueryKey', 'monitorQueryKey'],
  "ss.team.delete": ['teamsQueryKey', 'infiniteTeamsOptionsQueryKey', 'monitorQueryKey'],
  "ss.team.merge": ['teamsQueryKey', 'infiniteTeamsOptionsQueryKey', 'monitorQueryKey'],
  "ss.team.photo": ['teamsQueryKey', 'infiniteTeamsOptionsQueryKey', 'monitorQueryKey'],
  "ss.team.reel": ['gameUri', 'gameStreamQueryKey'],
  "ss.team.rename": ['teamsQueryKey', 'infiniteTeamsOptionsQueryKey', 'monitorQueryKey'],
  "ss.team.unmerge": ['teamsQueryKey', 'infiniteTeamsOptionsQueryKey', 'monitorQueryKey'],
  "ss.team.update": [
    'teamsQueryKey', 
    'teamStreamQueryKey', 
    'leaderboardQueryKey', 
    'infiniteTeamsOptionsQueryKey',
    'monitorQueryKey',
  ],
  "ss.personalization.delete": ['personalizationsUri'],
  "ss.personalization.update": ['personalizationsUri'],
  "ss.personalization.create": ['personalizationsUri'],
  "ss.group.create": ['groupsQueryKey'],
  "ss.group.delete": ['groupsQueryKey'],
  "ss.group.update": ['groupsQueryKey'],
  "ss.analytic.teams": ['analyticsQueryKey'],
  "ss.comment.create": ['infiniteAnswerCommentsQueryKey', 'monitorQueryKey'],
  "ss.comment.delete": ['infiniteAnswerCommentsQueryKey', 'monitorQueryKey'],
  "ss.comment.like": ['infiniteAnswerCommentsQueryKey', 'monitorQueryKey'],
  "ss.comment.unlike": ['infiniteAnswerCommentsQueryKey', 'monitorQueryKey'],
  "ss.npc.create": ['npcsQueryKey'],
  "ss.npc.update": ['npcsQueryKey'],
  "ss.npc.delete": ['npcsQueryKey'],
  "ss.zone.create": ['zonesQueryKey'],
  "ss.zone.update": ['zonesQueryKey'],
  "ss.zone.delete": ['zonesQueryKey'],
  "ss.waypoint.create": ['waypointsQueryKey', 'zonesQueryKey'],
  "ss.waypoint.update": ['waypointsQueryKey', 'zonesQueryKey'],
  "ss.waypoint.delete": ['waypointsQueryKey', 'zonesQueryKey'],
  "ss.admin.create": ['administratorsQueryKey'],
  "ss.admin.delete": ['administratorsQueryKey'],
  "ss.coordinator.create": ['coordinatorsQueryKey'],
  "ss.coordinator.delete": ['coordinatorsQueryKey'],
  "ss.user.ban": ['bannedQueryKey'],
  "ss.user.unban": ['bannedQueryKey'],
};

// Mutations are used to handle the websocket events optimistically
// by updating the state of the app without the need to refetch the data from the server
const mutations = {
  "ss.game.media": [useVideoRefresher, useNubsTaskRefresher, useNubsGameRefresher],
};

/**
 * Compare a page listener list against the affected entity ID.
 * Listener IDs and websocket payloads can mix strings and numbers, so matching is loose.
 * @param {Array<string|number>|undefined} listenerIds
 * @param {string|number|null|undefined} affectedId
 * @returns {boolean}
 */
export const listenerIncludesId = (listenerIds, affectedId) => {
  if (!Array.isArray(listenerIds)) return false
  if (listenerIds.includes("*")) return true
  if (affectedId == null) return false
  return listenerIds.some(id => String(id) === String(affectedId))
}

/**
 * Get the entity ID used by ID-scoped blue-bar listeners.
 * Comment events resolve to the parent answer so Monitor/Answer Detail can match loaded answers.
 * @param {{ detail?: object, source?: string }} payload
 * @returns {string|number|undefined}
 */
export const getAffectedId = ({ detail, source }) => {
  switch (source) {
    case "ss.team.create":
    case "ss.team.update":
    case "ss.team.delete":
    case "ss.team.merge":
    case "ss.team.photo":
    case "ss.team.rename":
    case "ss.team.unmerge":
    case "ss.game.join":
      return detail.team?.id;
    case "ss.task.create":
    case "ss.task.update":
    case "ss.task.delete":
      return detail.task?.id;
    case "ss.answer.create":
    case "ss.answer.accept":
    case "ss.answer.feature":
    case "ss.answer.unfeature":
    case "ss.answer.like":
    case "ss.answer.unlike":
    case "ss.answer.reject":
    case "ss.answer.update":
      return detail.answer?.id;
    case "ss.comment.create":
    case "ss.comment.delete":
    case "ss.comment.like":
    case "ss.comment.unlike":
      return detail.answer?.id;
    case "ss.game.delete":
    case "ss.game.update":
    case "ss.game.score":
    case "ss.game.rescore":
      return detail.game?.id;
    case "ss.reward.create":
    case "ss.reward.delete":
    case "ss.reward.update":
      return detail.reward?.id;
    case "ss.group.create":
    case "ss.group.delete":
    case "ss.group.update":
      return detail.group?.id;
    case "ss.personalization.create":
    case "ss.personalization.delete":
    case "ss.personalization.update":
      return detail.personalization?.id;
    case "ss.designation.create":
    case "ss.designation.delete":
    case "ss.designation.update":
      return detail.designation?.id;
    case "ss.npc.create":
    case "ss.npc.delete":
    case "ss.npc.update":
      return detail.npc?.id || detail.non_player_character?.id;
    case "ss.zone.create":
    case "ss.zone.delete":
    case "ss.zone.update":
      return detail.zone?.id;
    case "ss.waypoint.create":
    case "ss.waypoint.delete":
    case "ss.waypoint.update":
      return detail.waypoint?.id;
    case "ss.admin.create":
    case "ss.admin.delete":
      return detail.administrator?.user_id || detail.admin?.user_id;
    case "ss.coordinator.create":
    case "ss.coordinator.delete":
      return detail.coordinator?.user_id;
    case "ss.user.ban":
    case "ss.user.unban":
      return detail.ban?.user_id || detail.banned?.user_id || detail.user?.id;
    default:
      return detail?.relation?.id;
  }
}

const Mutation = ({ game_id, task_id, message, useMutation }) => {
  const mutate = useMutation(game_id, (task_id || message?.detail?.media?.relation?.id), message);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => mutate(message), []);
  return null;
};

const RefreshUris = ({ game_id: gameIdFromParams, source, message, shouldInvalidate = true, listeners }) => {
  const queryClient = useQueryClient();
  const location = useLocation()
  // get the current path name, e.g. /game/edit/123 -> game/edit/:id
  // root path is the same as the dashboard path
  const pathName = location?.pathname === ROUTE_PATTERNS.ROOT ? ROUTE_PATTERNS.DASHBOARD : location?.pathname
  const matchRoute = _path => matchPath(pathName, _path)

  // get the realtime updates received from the websocket event
  const [, setRealTimeUpdates] = useRecoilState(realTimeUpdatesState);

  // Destructure the websocket message to get relevant data
  const { detail, answer, task, team } = message;
  const { media } = detail || {};
  const { relation } = media || {};

  // populate instance ids for the query keys, they vary depending on the event
  // taskId can also be the relation.id if the event is related to media
  const gameId = gameIdFromParams || getGameId(message);
  const taskId = task?.id || answer?.task_id || relation?.id;
  const teamId = team?.id;
  const affectedId = getAffectedId({ detail, source });
  
  useEffect(() => {
    if (gameId !== "new" && shouldInvalidate) {
      for (let uriFn of refreshUris[source] || []) {
        // Query keys are built differently for each query states;
        // their parameters do not alway match with uriFn signatures (game_id, message),
        // thus it needs to be normalized manually if necessary.
        const uriFnNormalized = {
          monitorQueryKey: monitorQueryKey(gameId, taskId, teamId),
          leaderboardQueryKey: leaderboardQueryKey(gameId),
          // Use actual dashboard query key that is being used by the dashboard page
          dashboardQueryKey: dashboardQueryKey(),
          teamsQueryKey: teamsQueryKey(gameId),
          teamStreamQueryKey: teamStreamQueryKey(gameId, teamId),
          rewardsQueryKey: rewardsQueryKey(gameId),
          challengesQueryKey: challengesQueryKey(gameId),
          groupsQueryKey: groupsQueryKey(gameId),
          gameUri: gameUri(gameId),
          gameBrandingUri: gameBrandingUri(gameId),
          gameStreamQueryKey: gameStreamQueryKey(gameId),
          slideshowQueryKey: slideshowQueryKey(gameId),
          personalizationsUri: personalizationsUri(gameId),
          analyticsQueryKey: analyticsQueryKey(gameId),
          answerQueryKey: answerQueryKey(message?.detail?.answer?.id),
          infiniteAnswerCommentsQueryKey: infiniteAnswerCommentsQueryKey(null, message),
          infiniteTeamsOptionsQueryKey: infiniteTeamsOptionsQueryKey(gameId).slice(0, -1),
          libraryQueryKey: libraryQueryKey(gameId),
          designationsQueryKey: designationsQueryKey(gameId),
          challengeStreamQueryKey: challengeStreamQueryKey(gameId, taskId),

          // Only invalidate the nubs for the specific task
          nubsQueryKey: nubsQueryKey(gameId, taskId),

          // Invalidate zone when a zone is created, updated or deleted
          zonesQueryKey: zonesQueryKey(gameId),

          // Invalidate zone when a waypoint is created, updated or deleted
          waypointsQueryKey: waypointsQueryKey(gameId),

          npcsQueryKey: npcsQueryKey(gameId),
          judgeQueryKey: judgeQueryKey(gameId),
          administratorsQueryKey: administratorsQueryKey(gameId),
          coordinatorsQueryKey: coordinatorsQueryKey(gameId),
          bannedQueryKey: bannedQueryKey(gameId),
        }
        let key = uriFnNormalized[uriFn];
        if (!key && typeof uriFn === 'function') {
            key = uriFn(gameId, message)
        }

        // Use functional update to avoid stale closure and dedupe by message.id
        setRealTimeUpdates(prev => {
          const newAffectedPaths = Object.keys(pathQueryKeyMap)
            .filter(path => pathQueryKeyMap[path].includes(uriFn))
            .map(path => ({
              path,
              shouldShowBlueBar: listenerIncludesId(listeners?.[source], affectedId) && matchRoute(path),
              shouldShowRealTimeDot: { 
                [gameId]: !matchRoute(path) && hasListener(path, source)
              },
            }));

          const existingIndex = prev.findIndex(update => update?.message?.id === message.id && JSON.stringify(update?.key) === JSON.stringify(key));

          // Insert when not present
          if (existingIndex === -1) {
            return [
              ...prev,
              {
                message: message,
                key: key,
                affectedPaths: newAffectedPaths,
              },
            ];
          }

          // Update existing entry: recompute flags for all affected paths
          const existing = prev[existingIndex];
          const existingPaths = (existing.affectedPaths || []).map(p => p.path);
          const newPaths = newAffectedPaths.map(p => p.path);
          const allPaths = Array.from(new Set([...existingPaths, ...newPaths]));

          const mergedAffectedPaths = allPaths.map(path => {
            const existingPath = existing.affectedPaths?.find(p => p.path === path);
            const newPath = newAffectedPaths.find(p => p.path === path);
            
            return {
              path,
              shouldShowBlueBar: listenerIncludesId(listeners?.[source], affectedId) && matchRoute(path),
              shouldShowRealTimeDot: {
                ...(existingPath?.shouldShowRealTimeDot || {}),
                ...(newPath?.shouldShowRealTimeDot || {}),
                // Only set to true if not on this path AND path has listeners for this source
                [gameId]: !matchRoute(path) && hasListener(path, source),
              },
            };
          });

          const updated = [...prev];
          updated[existingIndex] = {
            ...existing,
            // Keep the original key; only merge paths to avoid duplicates
            affectedPaths: mergedAffectedPaths,
          };

          return updated;
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, queryClient, source]);

  return null;
};

const RefresherInstance = ({ listeners }) => {
  const { game_id } = useParams();
  const location = useLocation();
  const message = useRefresh({ subscriptions: Object.keys(refreshUris).join(",") });
  const [wsMessage, setWsMessage] = useRecoilState(websocketState);

  useEffect(() => {
    if (message.detail) {
        setWsMessage([
            ...wsMessage,
            message,
        ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  if (message.id) {
    const gameId = getGameId(message);
    const m = mutations[message.source] || [];
    const isCurrentGame = parseInt(gameId) === parseInt(game_id)
    const isInstantUpdate = instantUpdateSources.includes(message.source)

    return (
      <>
        <RefreshUris
          game_id={game_id}
          key={message.id}
          source={message.source}
          message={message}
          shouldInvalidate={
            /**
             * Queries should invalidate on websocket events
             * only when it is coming from the same game or we're on the dashboard
             * and it is an instant update
             */
            isCurrentGame || ["/", "/dashboard"].includes(location.pathname) || isInstantUpdate
          }
          listeners={listeners}
        />
        {isCurrentGame &&
          m.map((mutation, mutationIndex) => (
            <Mutation
              game_id={game_id}
              key={`${message.id}-${mutationIndex}`}
              message={message}
              useMutation={mutation}
            />
          ))}
      </>
    );
  }

  return null;
};

const Noop = () => null;

const Refresher = ({ listeners, refreshCallbacks }) => {
  const { t } = useTranslation();
  const currentRouteKey = useCurrentRouteKey();
  const refreshData = useRefreshData();
  const [realTimeUpdates] = useRecoilState(realTimeUpdatesState);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const onRefresh = (updates) => {
    // Execute the refresh in the next frame to avoid race conditions
    window.requestAnimationFrame(async () => {
      // track the refresh state manually since we can't track query fetching state individually
      setIsRefreshing(true);
      // invalidate the queries affected by the event by batching unique keys
      try {
        await refreshData(updates, currentRouteKey)
        // execute the refresh callback if available
        const refreshCallback = refreshCallbacks?.[updates[0]?.message?.source]
        if (refreshCallback) {
          refreshCallback(updates[0]?.message?.id)
        }
      } finally {
        setIsRefreshing(false);
      }
    })
  }

  const currentRealTimeUpdates = useMemo(() => {
    return realTimeUpdates.filter((update, index) => {
      const affectsCurrentRoute = update.affectedPaths.some(p => p.path === currentRouteKey);
      if (!affectsCurrentRoute) return false;
      /**
       * We need to filter unique updates for the same message and key
       * This is because the same message can be sent multiple times, and we only want to show the first one
       * This is to avoid showing the same update multiple times in the callout
       */
      const messageIndex = realTimeUpdates.findIndex(updateItem =>
        updateItem.message.id === update.message.id &&
        JSON.stringify(updateItem.key) === JSON.stringify(update.key) &&
        updateItem.affectedPaths.some(p => p.path === currentRouteKey)
      );
      return messageIndex === index;
    });
  }, [realTimeUpdates, currentRouteKey])

  // Determine instant updates based on message source
  const instantUpdates = useMemo(() => {
    return currentRealTimeUpdates.filter(update =>
      instantUpdateSources.includes(update?.message?.source)
    )
  }, [currentRealTimeUpdates])

  // Automatically refresh instantly-matched updates and skip showing them in the queue/callout
  useEffect(() => {
    if (instantUpdates.length > 0) {
      onRefresh(instantUpdates)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instantUpdates])

  const queuedUpdates = currentRealTimeUpdates.filter((u, index, array) => 
    // Exclude instant updates from those shown in the callout queue
    !instantUpdateSources.includes(u?.message?.source) && 
    // Check if the update must be shown in the callout queue
    u?.affectedPaths.some(p => p.shouldShowBlueBar)
  )

  // render the refresher instance in the app-container-children
  const container = document.getElementById('app-container-children')
  
  if (!container) {
    return null
  }

  // Display the first real time update in the callout if available, and group the rest of the updates
  const [realTimeUpdate, ...restUpdates] = queuedUpdates.filter(
    // Filter by unique message source
    (u, index, array) => array.findIndex(u2 => u2?.message?.source === u?.message?.source) === index
  )
  const eventTemplateParts = getEventTemplateParts(realTimeUpdate?.message?.source)
  
  return createPortal(
    <Suspense fallback={<Noop />}>
      <RefresherInstance
        listeners={listeners}
      />
      {
        realTimeUpdate && queuedUpdates.length > 0 ? (
          <Callout key={realTimeUpdate.key?.toString()} data-route-key={currentRouteKey}>
            {
              isRefreshing ? (
                <>
                  <Spinner size="0.875rem" /><span>{t('Retrieving page updates...')}</span>
                </>
              ) : (
                <>
                  {
                    t('{{eventKey}} has been {{eventAction}}', {
                      eventKey: eventTemplateParts?.eventKey,
                      eventAction: eventTemplateParts?.eventAction,
                      count: restUpdates.length
                    })
                  } {restUpdates.length > 0 ? `+${restUpdates.length}` : ''} 
                  <Button className="mb-0" onClick={() => onRefresh(currentRealTimeUpdates)}>
                    <FontAwesomeIcon icon={faRedo} /> {t('Refresh')}
                  </Button>
                </>
              )
            }
          </Callout>
        ) : null
      }
    </Suspense>
  , container);
};

export default Refresher;